import { useMemo, useState } from 'react';
import { LOGIC_CATEGORIES, useLogicBlocks } from '@morph-mapper/node-logic';
import { getKeys } from '@morph-mapper/utils';
import { useStore } from '@/store';
import { LogicBlockView } from './types';

type NodeSelectCategory = {
  category: keyof typeof LOGIC_CATEGORIES;
  blocks: LogicBlockView[];
};

/**
 * Hooks which allows querying logic blocks by category and title.
 * It returns a list of categories and their corresponding blocks that satisfy the query.
 */
export const useQueryLogicBlocks = () => {
  /** Global state */
  const variant = useStore(({ config: cfg }) => cfg.getVariant());
  /** Local state */
  const [query, setQuery] = useState('');
  /** Hooks */
  const { logicBlocksByCategory } = useLogicBlocks(variant);

  const categories = useMemo<NodeSelectCategory[]>(() => {
    const normalized = query.trim().toLowerCase();

    return getKeys(LOGIC_CATEGORIES)
      .map((category) => {
        const blocks = logicBlocksByCategory[category];
        // If the query is empty, return all blocks
        if (!normalized) return { category, blocks };

        const filteredBlocks = blocks.filter((block) => {
          const haystack = [block.title, block.description]
            .join(' ')
            .toLowerCase();
          return haystack.includes(normalized);
        });

        return { category, blocks: filteredBlocks };
      })
      .filter(({ blocks }) => blocks.length > 0 || !normalized);
  }, [logicBlocksByCategory, query]);

  return { categories, query, setQuery };
};
